import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PlanVersionSummary, StateWorkspaceSummary } from '@/lib/apiTypes'
import { usePlanVersions, usePlanVersion, useStateVersions, useStateFindings, useStateLock } from '@/lib/hooks'
import { WorkspaceProvidersTab } from '@/shared/components/WorkspaceProvidersTab'
import { ResourceInspector } from '@/features/state/ResourceInspector'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { PlanRefGroup } from '@/features/plans/components/PlanRefGroup'
import { ReplacementsByProviderCallout } from '@/features/plans/components/ReplacementsByProviderCallout'
import { replacementCauseMap } from '@/features/plans/lib/findings'
import { PlanFindingsList } from '@/features/plans/tabs/FindingsTab'
import { PlanPolicyTab } from '@/features/plans/tabs/PolicyTab'
import { PlanSummaryTab } from '@/features/plans/tabs/SummaryTab'
import { PLAN_TAB_LABEL, type PlanDetailTab } from '@/features/plans/tabs/planTabs'
import { Tabs } from '@/shared/components/Tabs'

export function PlanWorkspaceDetail({
  orgId, workspace, canAdmin, defaultVersionId, onBack, onRefreshWorkspaces,
}: {
  orgId: string
  workspace: StateWorkspaceSummary
  canAdmin: boolean
  defaultVersionId?: string
  onBack: () => void
  onRefreshWorkspaces: () => void
}) {
  const navigate = useNavigate()
  const versions = usePlanVersions(orgId, workspace.name)
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(defaultVersionId ?? null)
  const [detailTab, setDetailTab] = useState<PlanDetailTab>('summary')
  const [showInspector, setShowInspector] = useState(false)

  // State-backend context (current serial + inspector data source).
  const stateVersions = useStateVersions(orgId, workspace.name)
  const stateFindings = useStateFindings(orgId, workspace.name)
  const stateLock = useStateLock(orgId, workspace.name)

  const list = versions.data?.versions ?? []

  useEffect(() => {
    if (!list.length) return
    if (selectedVersionId && list.some((v) => v.id === selectedVersionId)) return
    setSelectedVersionId(list[0].id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.data])

  useEffect(() => { if (defaultVersionId) setSelectedVersionId(defaultVersionId) }, [defaultVersionId])

  const planDetail = usePlanVersion(orgId, workspace.name, selectedVersionId)
  const selectedVersion = planDetail.data?.version ?? list.find((v) => v.id === selectedVersionId) ?? null
  const findings = planDetail.data?.findings ?? []

  function selectVersion(id: string) {
    setSelectedVersionId(id)
    navigate(`/org/${orgId}/plans/${encodeURIComponent(workspace.name)}/${id}`, { replace: true })
  }

  // Group versions by plan_ref, preserving first-seen order (list is newest-first).
  const groups: { ref: string | null; versions: PlanVersionSummary[] }[] = []
  const seen = new Map<string, PlanVersionSummary[]>()
  for (const v of list) {
    const key = v.plan_ref ?? '\u0000default'
    if (!seen.has(key)) { const arr: PlanVersionSummary[] = []; seen.set(key, arr); groups.push({ ref: v.plan_ref, versions: arr }) }
    seen.get(key)!.push(v)
  }

  const latestStateVersion = stateVersions.data?.versions?.[0] ?? null
  const replacementCauses = replacementCauseMap(findings)

  return (
    <div>
      {showInspector && latestStateVersion && (
        <ResourceInspector
          orgId={orgId}
          workspace={workspace.name}
          versions={stateVersions.data?.versions ?? []}
          selectedVersionId={latestStateVersion.id}
          lockActive={!!stateLock.data?.lock}
          findings={stateFindings.data?.findings ?? []}
          replacementCauses={replacementCauses}
          onViewFindings={() => { setShowInspector(false); setDetailTab('findings') }}
          onMutated={() => { stateVersions.refetch(); stateFindings.refetch() }}
          onClose={() => setShowInspector(false)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack} title="Back to all workspaces">←</button>
        <h2 style={{ fontFamily: 'var(--head)', fontSize: '1.1rem', color: 'var(--text)', margin: 0 }}>{workspace.name}</h2>
        {workspace.latest_serial !== null && <span className="badge badge-neutral">state serial {workspace.latest_serial}</span>}
        {workspace.blocked === 1 && <span className="badge badge-red">policy blocked</span>}
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" disabled={versions.refreshing} onClick={() => versions.refetch()}>
          {versions.refreshing ? <span className="spinner" /> : '↻'}
        </button>
      </div>

      {/* Grouped plan cards */}
      <div className="eyebrow">
        Plans by ref
      </div>
      {versions.loading && <SkeletonTable headers={['Plan']} rows={3} firstColTwoLines={false} />}
      {!versions.loading && list.length === 0 && (
        <EmptyState icon="◈" title="No plans for this workspace yet." subtitle="Push a redacted `terraform show -json` plan to PUT /plans/<key>.tfplan to populate this view." />
      )}
      {groups.map((g) => (
        <PlanRefGroup
          key={g.ref ?? '\u0000default'}
          group={g}
          currentSerial={workspace.latest_serial}
          selectedVersionId={selectedVersionId}
          onSelect={selectVersion}
        />
      ))}

      {/* Per-plan tabbed detail */}
      {selectedVersion && (
        <div style={{ marginTop: 24 }} data-testid="plan-detail-panel">
          <Tabs
        tabs={(['summary', 'findings', 'policy', 'providers'] as PlanDetailTab[]).map((key) => ({
          key,
          label: PLAN_TAB_LABEL[key],
          badge:
            key === 'findings' && findings.length > 0 ? (
              <span className="badge badge-neutral" style={{ fontSize: '0.58rem' }}>{findings.length}</span>
            ) : key === 'policy' && workspace.blocked === 1 ? (
              <span className="badge badge-red" style={{ fontSize: '0.58rem' }}>!</span>
            ) : undefined,
        }))}
        value={detailTab}
        onChange={setDetailTab}
        label="Plan sections"
        right={
          <button className="btn btn-ghost btn-sm" onClick={() => setShowInspector(true)} title="Inspect the resources this plan targets (current state)">Resources</button>
        }
      />

          {detailTab === 'summary' && (
            <PlanSummaryTab orgId={orgId} workspaceName={workspace.name} version={selectedVersion} findings={findings} currentSerial={workspace.latest_serial} loading={planDetail.loading} />
          )}
          {detailTab === 'findings' && (
            <PlanFindingsList findings={findings} sensitiveCount={selectedVersion.sensitive_change_count} loading={planDetail.loading} />
          )}
          {detailTab === 'policy' && (
            <PlanPolicyTab
              orgId={orgId}
              workspace={workspace}
              version={selectedVersion}
              canAdmin={canAdmin}
              onApproved={() => { versions.refetch(); onRefreshWorkspaces() }}
            />
          )}
          {detailTab === 'providers' && (
            <div>
              <ReplacementsByProviderCallout orgId={orgId} />
              <WorkspaceProvidersTab orgId={orgId} workspace={workspace.name} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
